import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { History, User, X } from "lucide-react";
import { fetchOrderStatusHistory } from "../../api/OrderAPIs";
import { canMutateOrders } from "../../utils/roles";
import { useAuth } from "../../utils/auth";

const formatHistoryTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Africa/Cairo",
    day: "numeric",
    month: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(date);

  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  const period = (get("dayPeriod") || "").replace(/\./g, "").toUpperCase();
  return `${get("day")}/${get("month")}/${get("year")} ${get("hour")}:${get("minute")} ${period}`;
};

const actorLabel = (value) => {
  if (!value || value.toLowerCase() === "system") return "System";
  return value;
};

export const OrderStatusHistoryTimeline = ({ orderId, hideTitle = false }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["orderStatusHistory", orderId],
    queryFn: () => fetchOrderStatusHistory(orderId),
    enabled: !!orderId,
  });

  const entries = useMemo(
    () => [...(data?.entries || data?.Entries || [])],
    [data]
  );

  if (!orderId) return null;

  return (
    <div className="text-right" dir="rtl">
      {!hideTitle && (
        <div className="font-bold text-slate-900 flex items-center gap-1.5 mb-3">
          <History size={16} />
          سجل تحديثات الحالة
        </div>
      )}
      {isLoading && <div className="text-sm text-slate-600">جاري تحميل السجل...</div>}
      {error && (
        <div className="text-sm text-red-600">
          {error.message || "فشل تحميل سجل التحديثات"}
        </div>
      )}
      {!isLoading && !error && entries.length === 0 && (
        <div className="text-sm text-slate-500">
          لا يوجد سجل بعد. التغييرات من الآن هتظهر هنا.
        </div>
      )}
      {entries.length > 0 && (
        <div className="space-y-2">
          {entries.map((entry) => (
            <div
              key={entry.id || entry.Id}
              className="relative pr-5 border-r-2 border-indigo-200 pb-2"
            >
              <div className="absolute right-[-5px] top-1.5 w-2.5 h-2.5 rounded-full bg-indigo-600" />
              <div className="font-semibold text-gray-800 text-sm">
                {entry.status || entry.Status || "—"}
              </div>
              <div className="text-[12px] text-gray-500 mt-0.5">
                {formatHistoryTime(entry.changedAt || entry.ChangedAt)}
              </div>
              <div className="text-[12px] text-indigo-700 mt-0.5 flex items-center gap-1">
                <User size={12} />
                {actorLabel(entry.changedBy || entry.ChangedBy)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const StatusHistoryModal = ({ orderId, onClose }) => {
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, []);

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/60 backdrop-blur-sm"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={onClose}
      role="presentation"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] flex flex-col overflow-hidden"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="سجل التحديثات"
      >
        <div className="bg-slate-800 text-white px-4 py-3 flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-slate-300">سجل التحديثات</p>
            <p className="font-bold text-lg truncate">طلب #{orderId}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-xl hover:bg-white/10 transition shrink-0"
            aria-label="إغلاق"
          >
            <X size={18} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <OrderStatusHistoryTimeline orderId={orderId} hideTitle />
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

const OrderStatusHistoryButton = ({
  orderId,
  className = "",
  compact = false,
}) => {
  const { authState } = useAuth();
  const canView = canMutateOrders(authState.role);
  const [open, setOpen] = useState(false);

  if (!orderId || !canView) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={
          className ||
          (compact
            ? "w-full bg-slate-100 text-slate-800 border border-slate-300 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-1 active:scale-95 transition hover:bg-slate-200"
            : "w-full bg-slate-700 text-white py-2.5 rounded-xl text-sm font-medium flex items-center justify-center gap-1.5 active:scale-95 transition hover:bg-slate-800")
        }
        title="عرض سجل التحديثات"
      >
        <History size={compact ? 12 : 14} />
        عرض سجل التحديثات
      </button>
      {open ? (
        <StatusHistoryModal orderId={orderId} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
};

export default OrderStatusHistoryButton;
